import { AUTH_ROUTE, TOKEN_ROUTE, REGISTER_ROUTE, LOGOUT_ROUTE, REFRESH_ROUTE, USER_ROUTE, RESET_ROUTE, REQUEST_RESET_ROUTE, TOKEN_RESET_ROUTE } from './consts';
import { axiosInstance, axiosPublicInstance } from './instance';

export type AuthTokens = {
  access?: string;
  refresh?: string;
  user?: any;
};

export async function login(username: string, password: string): Promise<AuthTokens> {
  try {
    const response = await axiosInstance.post<AuthTokens>(`${AUTH_ROUTE}${TOKEN_ROUTE}`, { username, password });
    const { access, refresh } = response.data ?? {};
    if (access) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access}`;
    }
    return { access, refresh };
  } catch (err: any) {
    return Promise.reject(err?.response?.data ?? err);
  }
}

export type RegisterRequest = {
  email: string;
  username: string;
  password: string;
  password2: string;
  firstName: string;
  lastName: string;
};

export async function register(payload: RegisterRequest): Promise<void> {
  try {
    await axiosInstance.post(`${AUTH_ROUTE}${REGISTER_ROUTE}`, {
      email: payload.email,
      username: payload.username,
      password: payload.password,
      password2: payload.password2,
      first_name: payload.firstName,
      last_name: payload.lastName,
    });
  } catch (err: any) {
    return Promise.reject(err?.response?.data ?? err);
  }
}

export async function logout(): Promise<void> {
  try {
    await axiosInstance.post(LOGOUT_ROUTE);
  } catch {
    // treat as logged out regardless
  }
  delete axiosInstance.defaults.headers.common['Authorization'];
}

export async function getUserInfo(): Promise<{ data: any; error: Error | null }> {
  try {
    const response = await axiosInstance.get(USER_ROUTE);
    return { data: response.data, error: null };
  } catch (err: any) {
    return { data: null, error: new Error(err?.response?.status === 401 ? 'UNAUTHORIZED' : err) };
  }
}

export async function updateUserInfo(username: string, email: string, firstName?: string, lastName?: string): Promise<{ data: any; error: Error | null }> {
  try {
    const response = await axiosInstance.post(USER_ROUTE, {
      username,
      email,
      ...(firstName !== undefined && { first_name: firstName }),
      ...(lastName !== undefined && { last_name: lastName }),
    });
    return { data: response.data, error: null };
  } catch (err: any) {
    return { data: null, error: new Error(err?.response?.data?.detail ?? err) };
  }
}

export async function resetUserPassword(old_password: string, new_password: string): Promise<{ data: any; error: Error | null }> {
  try {
    const response = await axiosInstance.put(RESET_ROUTE, { old_password, new_password });
    return { data: response.data, error: null };
  } catch (err: any) {
    return { data: null, error: new Error(err?.response?.data?.detail ?? err) };
  }
}

export async function requestPasswordReset(email: string): Promise<string> {
  try {
    const response = await axiosPublicInstance.post<{ detail?: string }>(REQUEST_RESET_ROUTE, { email });
    return response.data?.detail ?? 'Reset link sent. Check your email.';
  } catch (err: any) {
    return Promise.reject(err?.response?.data ?? err);
  }
}

export async function resetUserPasswordToken(token: string, password: string): Promise<string> {
  try {
    const response = await axiosPublicInstance.post<{ detail?: string }>(TOKEN_RESET_ROUTE, { token, password });
    return response.data?.detail ?? 'Password reset successfully.';
  } catch (err: any) {
    return Promise.reject(err?.response?.data ?? err);
  }
}

export async function refreshTokens(refreshToken?: string | null): Promise<AuthTokens> {
  try {
    const response = await axiosInstance.post<AuthTokens>(REFRESH_ROUTE, refreshToken ? { refresh: refreshToken } : {});
    const { access, refresh } = response.data ?? {};
    if (access) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access}`;
    }
    return { access, refresh };
  } catch (err) {
    delete axiosInstance.defaults.headers.common['Authorization'];
    throw err;
  }
}
